<template>
  <div
    class="step-holder"
    v-bind:class="{ completed: activeJobEditStep > 4, active: activeJobEditStep === 4 }"
  >
    <div class="title-bar">
      <div style="width: 100%">
        <div style="display: flex; justify-content: space-between">
          <h3><span>4. Lenses</span></h3>
          <div>
            <a @click="editThisStep" v-if="activeJobEditStep > 4 && !pdError">Change</a
            ><a class="ml-4" @click="resetThisStep" v-if="activeJobEditStep > 4">Reset</a>
          </div>
        </div>
        <ErrorMessage v-if="activeJobEditStep > 4 && pdError" type="box">{{ pdError }}</ErrorMessage>
        <div class="selected-features" v-else-if="activeJobEditStep > 4 && selectedLens">
          <template v-if="selectedLens.name">
            <div><b>Supplier</b>{{ getLensSupplierName(selectedLens.supplier_id) }}</div>
            <div>
              <b>Lens</b>{{ selectedLens.name }}
              <b-badge v-if="selectedLens.is_upgrade && selectedLens.upgrade_reason" variant="warning" class="ml-2">UPGRADED</b-badge>
            </div>
            <div><b>Color</b>{{ lensColor }}</div>
            <div>
              <b>Coating</b
              >{{ selectedLens.coating + (lensCoating ? ' - ' : '') + (lensCoating || '') }}
            </div>
            <div v-if="selectedLens.selected_lens_mod"><b>Lens Style</b>{{ selectedLens.selected_lens_mod.name }} ({{ selectedLens.selected_lens_mod.code }})</div>
            <div v-if="selectedLens.selected_lens_mod_corridor"><b>Corridor</b>{{ selectedLens.selected_lens_mod_corridor.name }} ({{ selectedLens.selected_lens_mod_corridor.code }})</div>
          </template
          ><template v-else>
            <div><b>Supplier</b>{{ selectedLens.lenses.supplier.name }}</div>
            <div>
              <b>Lens</b>{{ selectedLens.lenses.name }}
              <b-badge v-if="selectedLens.is_upgrade && selectedLens.upgrade_reason" variant="warning" class="ml-2">UPGRADED</b-badge>
            </div>
            <div><b>Color</b>{{ lensColor }}</div>
            <div>
              <b>Coating</b
              >{{ selectedLens.lenses.coating + (lensCoating ? ' - ' : '') + (lensCoating || '') }}
            </div>
            <div v-if="selectedLens.selected_lens_mod"><b>Lens Style</b>{{ selectedLens.selected_lens_mod.name }} ({{ selectedLens.selected_lens_mod.code }})</div>
            <div v-if="selectedLens.selected_lens_mod_corridor"><b>Corridor</b>{{ selectedLens.selected_lens_mod_corridor.name }} ({{ selectedLens.selected_lens_mod_corridor.code }})</div>
          </template>
        </div>
      </div>
    </div>
    <ValidationObserver v-slot="{ handleSubmit, invalid }">
      <b-form @submit.stop.prevent="handleSubmit(sendLensForm)" v-if="activeJobEditStep === 4">
        <ValidationProvider name="Lenses" rules="required" v-slot="validationContext">
          <LensList
            ref="lensList"
            :customer="customer"
            :prescription="prescription"
            :showTitle="true"
            :selectable="true"
            :collapsible="true"
            v-model="$store.state.jobsModule.selectedLens"
            :state="getValidationState(validationContext)"
            @error-state="handleLensError"
            @update:availableLenses="handleAvailableLensesUpdate"
            @upgrade-reason-input="handleUpgradeReasonInput"
          ></LensList>
          <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
        </ValidationProvider>
        
        <!-- Grind lens validation message -->
        <div v-if="grindLensValidationMessage" class="mt-2">
          <div class="alert alert-warning" role="alert">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            {{ grindLensValidationMessage }}
          </div>
        </div>
        
        <div class="confirmation-wrapper" v-if="!hasLensError">
          <b-button
            class="mr-3"
            variant="outline-secondary"
            @click="setActiveJobEditStep(5)"
            type="button"
            :disabled="!isLensSelected"
            >Cancel</b-button
          >
          <b-button
            class="button-with-loader"
            type="submit"
            :disabled="invalid || !isLensSelected || hasLensError || !!grindLensValidationMessage"
            variant="primary"
            >Confirm</b-button
          >
        </div>
      </b-form>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import LensList from '../../LensList.vue';
import ErrorMessage from '@/components/ErrorMessage.vue';

export default {
  name: 'Lenses',
  props: {
    customer: Object,
    prescription: Object,
    orderLens: Object,
  },
  data() {
    return {
      hasLensError: false,
      currentAvailableLenses: [],
    };
  },
  watch: {
    selectedLens: {
      handler(newVal) {
        if (newVal) {
          this.hasLensError = false;
        }
      },
    },
  },
  computed: {
    ...mapState({
      activeJobEditStep: state => state.jobsModule.activeJobEditStep,
      selectedLens: state => state.jobsModule.selectedLens,
      selectedLensCoating: state => state.jobsModule.selectedLensCoating,
      selectedPd: state => state.jobsModule.selectedPd,
    }),
    // DP-5720: no lens can be picked for a PD this lens type cannot use (DP-5683), so Change stays closed
    pdError() {
      return this._.get(this.selectedPd, 'pdPresenter.error') ? this.selectedPd.pdPresenter.message : null;
    },
    isLensSelected() {
      const hasValidLens = this.selectedLens &&
        this.currentAvailableLenses.length > 0 &&
        this.currentAvailableLenses.some(
          lens => lens.id === this.selectedLens.id || lens.lens_id === this.selectedLens.id,
        );
      
      // For grind lenses, also check if upgrade reason is provided
      if (hasValidLens && this.selectedLens.is_upgrade) {
        return !!this.selectedLens.upgrade_reason;
      }
      
      return hasValidLens;
    },
    lensCoating() {
      if (this.selectedLensCoating && this.selectedLens && this.selectedLens.lenses) {
        const seletedCoating = this._.find(this.selectedLensCoating, {
          lens_id: this.selectedLens.lenses.id,
        });
        return (
          this._.get(seletedCoating, 'coating.display_name') ||
          this._.get(seletedCoating, 'coating.name')
        );
      }
      return (
        this._.get(
          this.orderLens,
          'additional_info.data.lens_addon_combination.coating.display_name',
        ) || this._.get(this.orderLens, 'additional_info.data.lens_addon_combination.coating.name')
      );
    },
    lensColor() {
      const getColor = this._.get(this.selectedLens, 'lens_color.name');
      if (getColor) {
        return getColor;
      }
      return this._.get(this.selectedLens, 'lenses.lens_color.name');
    },
    
    grindLensValidationMessage() {
      if (this.selectedLens && this.selectedLens.is_upgrade && !this.selectedLens.upgrade_reason) {
        return 'Please provide a reason for the lens upgrade before proceeding.';
      }
      return '';
    },
  },
  components: {
    LensList,
    ErrorMessage,
    ValidationProvider,
    ValidationObserver,
  },
  methods: {
    editThisStep() {
      this.$store.commit('jobsModule/SET_ACTIVE_JOB_EDIT_STEP', 4);
    },
    resetThisStep() {
      this.$store.commit('jobsModule/SET_SELECTED_LENS', null);
      this.$store.commit('jobsModule/SET_ACTIVE_JOB_EDIT_STEP', 4);
      if (this.selectedLensCoating) {
        this.$store.commit('jobsModule/SET_SELECTED_LENS_COATING', null);
      }
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    sendLensForm() {
      if (this.selectedLens && this.selectedLens.is_upgrade && !this.selectedLens.upgrade_reason) {
        this.$store.dispatch('showErrorAlert', 'Please provide a reason for the lens upgrade before proceeding.');
        return;
      }
      this.$emit('lensDataSent', this.selectedLens);
      this.$store.commit('jobsModule/SET_ACTIVE_JOB_EDIT_STEP', 5);
    },
    handleLensError(hasError) {
      this.hasLensError = hasError;
      if (hasError) {
        this.$store.commit('jobsModule/SET_SELECTED_LENS', null);
      }
    },
    handleAvailableLensesUpdate(lenses) {
      this.currentAvailableLenses = lenses;
    },
    
    handleUpgradeReasonInput({ lens, reason }) {
      // Update the selected lens in the store with the new reason
      if (this.selectedLens && (this.selectedLens.id === lens.id || this.selectedLens.lens_id === lens.lens_id)) {
        const updatedLens = { ...this.selectedLens, upgrade_reason: reason };
        this.$store.commit('jobsModule/SET_SELECTED_LENS', updatedLens);
      }
    },
  },
};
</script>

<style scoped></style>
